/**
 * useDemoTracking.js
 * ──────────────────
 * Tracks page visits and time spent during a demo session.
 * Flushes accumulated data to the backend on page change and on
 * tab close / navigation away (via beforeunload + Page Visibility API).
 *
 * Usage:
 *   Add <DemoTracker /> inside ProtectedLayout when isDemo is true.
 *   It renders nothing visible.
 *
 * Data sent to POST /demo-sessions/flush/:
 *   { email, pages_visited: { "/dashboard": 45, "/members": 120 }, total_seconds }
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const FLUSH_INTERVAL_MS = 60_000; // passive flush every 60s

/**
 * Sends accumulated session data to the backend.
 * Uses sendBeacon when available (reliable on tab close), falls back to fetch.
 */
function flushSession(email, pagesVisited, totalSeconds) {
  if (!email || totalSeconds < 2) return false;

  const payload = JSON.stringify({
    email,
    pages_visited: pagesVisited,
    total_seconds: Math.round(totalSeconds),
  });

  const url = `${BACKEND}/demo-sessions/flush/`;

  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    return navigator.sendBeacon(url, blob);
  }
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {});
  return true;
}

/**
 * Hook — wire into any component that lives inside the demo session.
 * @param {string} email  The demo visitor's email (from gate form localStorage)
 */
export function useDemoTracking(email) {
  const location        = useLocation();
  const pageRef         = useRef(location.pathname);
  const pageStartRef    = useRef(Date.now());
  const pagesRef        = useRef({});   // { "/path": secondsSpent }
  const totalRef        = useRef(0);
  // D241: the backend ADDS every flush to the lead's running totals, so we must
  // send only the time accumulated since the last successful flush (a delta).
  // Previously every flush sent the cumulative session total, so each 60s tick,
  // tab-hide and unmount re-added the whole session -- inflating Time Spent
  // (e.g. 30h+ recorded for a one-hour session).
  const sentPagesRef    = useRef({});
  const sentTotalRef    = useRef(0);

  const flushDelta = (addr) => {
    const elapsed = (Date.now() - pageStartRef.current) / 1000;
    const pages = {
      ...pagesRef.current,
      [pageRef.current]: (pagesRef.current[pageRef.current] || 0) + elapsed,
    };
    const total = totalRef.current + elapsed;
    const dPages = {};
    Object.entries(pages).forEach(([p, s]) => {
      const d = s - (sentPagesRef.current[p] || 0);
      if (d > 0) dPages[p] = d;
    });
    const dTotal = total - sentTotalRef.current;
    if (dTotal < 2) return;
    if (flushSession(addr, dPages, dTotal)) {
      sentPagesRef.current = pages;
      sentTotalRef.current = total;
    }
  };

  // Accumulate time on current page when route changes
  useEffect(() => {
    const prev    = pageRef.current;
    const elapsed = (Date.now() - pageStartRef.current) / 1000;

    if (prev && elapsed > 0) {
      pagesRef.current[prev] = (pagesRef.current[prev] || 0) + elapsed;
      totalRef.current += elapsed;
    }

    pageRef.current      = location.pathname;
    pageStartRef.current = Date.now();
  }, [location.pathname]);

  // Periodic silent flush (every minute) — keeps data fresh if tab is left open
  useEffect(() => {
    if (!email) return;
    const timer = setInterval(() => {
      flushDelta(email);
    }, FLUSH_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [email]);

  // Flush on tab close / page hide / component unmount (logout)
  useEffect(() => {
    if (!email) return;

    const handleUnload = () => {
      flushDelta(email);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') handleUnload();
    };

    window.addEventListener('beforeunload', handleUnload);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibility);
      handleUnload(); // flush on logout / unmount
    };
  }, [email]);
}
